import { DuplicateDebtor } from "us.collection.debtor/Interfaces";
import { clearText } from "us.collection.debtor/functions";
import { DebtorFilterBy, PropertyNames } from "../Constants";
import { europeanCountry, Gender } from "us.common/constants";
import { i18n } from "us.helper";
import _ from "lodash";
import { IOnFilter, IOnSort } from "us.common/components";

/**
 * @description Get retaining debtor data.
 * @param {Array<DuplicateDebtor>} duplicatingDebtors - All duplicating debtors.
 * @returns Returns retaining debtor.
 */
export const getRetainingDebtor = (
  duplicatingDebtors: DuplicateDebtor[]
): DuplicateDebtor | undefined => {
  try {
    if (Array.isArray(duplicatingDebtors)) {
      return _.find(duplicatingDebtors, { isSelectedRetaining: true });
    } else {
      return undefined;
    }
  } catch (e) {
    return undefined;
  }
};

/**
 * @description Filter duplicate debtors and retaining debtors.
 * @param {Array<DuplicateDebtor>} duplicatingDebtors - All duplicating debtors.
 * @param {Array<DebtorFilterBy>} filterBy - Current selected filter.
 * @returns Returns filtered debtors.
 */
export const filterDuplicateDebtors = (
  duplicatingDebtors: DuplicateDebtor[],
  filterBy: Array<DebtorFilterBy>
): Array<any> => {
  try {
    if (Array.isArray(duplicatingDebtors)) {
      const debtorsWithoutSelectedRetain = _.filter(
        duplicatingDebtors,
        ({ isSelectedRetaining }) => !isSelectedRetaining
      );

      const caseDebtor = _.find(duplicatingDebtors, {
        isCaseDebtor: true,
      });

      if (caseDebtor) {
        const { firstName, lastName, zipCode, nin } = caseDebtor ?? {};

        // Filter by currently selected name, person no or zip code
        return debtorsWithoutSelectedRetain?.filter((debtor) => {
          const isNameEqual =
            clearText(debtor?.firstName) == clearText(firstName) &&
            clearText(debtor?.lastName) == clearText(lastName);
          const isPersonNoEqual = clearText(debtor?.nin) == clearText(nin);
          const isZipCodeEqual =
            clearText(debtor?.zipCode) == clearText(zipCode);

          let filterResult = true;

          if (filterBy.includes(DebtorFilterBy.NAME)) {
            filterResult = filterResult && isNameEqual;
          }
          if (filterBy.includes(DebtorFilterBy.PERSON_NO)) {
            filterResult = filterResult && isPersonNoEqual;
          }
          if (filterBy.includes(DebtorFilterBy.ZIP_CODE)) {
            filterResult = filterResult && isZipCodeEqual;
          }

          return filterResult;
        });
      } else {
        return debtorsWithoutSelectedRetain;
      }
    } else {
      return [];
    }
  } catch (e) {
    return [];
  }
};

/**
 * @description Check if current debtor is retainable.
 * @param {any} duplicateDebtor - Selected retaining debtor.
 * @returns Returns boolean.
 */
export const isRetainingSelectable = (duplicateDebtor: any): boolean => {
  try {
    if (duplicateDebtor) {
      const debtorFullName =
        clearText(duplicateDebtor?.firstName) +
        clearText(duplicateDebtor?.lastName);
      const personNo = clearText(duplicateDebtor?.nin);
      const zipCode = clearText(duplicateDebtor?.zipCode);

      return !debtorFullName || !personNo || !zipCode || duplicateDebtor?.isBadDebtor;
    } else {
      return false;
    }
  } catch (e) {
    return false;
  }
};

/**
 * @description Returns proper value to display in debtor UI field.
 * @param {DuplicateDebtor} debtor - value to be checked.
 * @param {PropertyNames} propertyName - value to be checked.
 * @returns Returns valid value.
 */
export const getFelidValue = (
  debtor: any,
  propertyName: PropertyNames
): string => {
  try {
    if (debtor[propertyName] && debtor[propertyName]?.toString()?.trim() !== "") {
      if (propertyName == PropertyNames.COUNTRY_CODE) {
        const country = _.find(europeanCountry, {
          value: debtor?.countryCode?.trim()?.toUpperCase(),
        })?.label;
        return country ? country : "-";
      } else if (propertyName == PropertyNames.GENDER) {
        return debtor?.gender == Gender.MALE
          ? i18n.t("US.COMMON:COMMON.MALE")
          : i18n.t("US.COMMON:COMMON.FEMALE");
      } else {
        return debtor[propertyName];
      }
    } else {
      return "-";
    }
  } catch (e) {
    return "-";
  }
};

/**
   * @function
   * @description - To get the table values sorted
   * @param {*} sortData - call back function
   * @param {*} dataSource - the data set of the table
   * @return {*} - sorted data
   */
export const handleSort: IOnSort = (sortData, dataSource) => {
  try {
    return sortData(
      dataSource.map((i: any) => {
        return {
          ...i,
          children: sortData(i.children),
        };
      })
    );
  } catch (error) {
    return dataSource;
  }
};

/**
 * @function
 * @description - To get the table values filter
 * @param searchData - call back function
 * @param dataSource - the data set of table
 * @returns - sorted data
 */
export const handleFilter: IOnFilter = (searchData, dataSource) => {
  let result: any[] = [];
  try {
    dataSource.flatMap((i: any) => {
      searchData(i?.children)?.length != 0 &&
        result.push({
          ...i,
          children:
            searchData(i?.children)?.length == 0
              ? []
              : searchData(i?.children),
        });
    });
    return result;
  } catch (error) {
    return result;
  }
};


/**
 * @description Chack Retained debtor is difference with case debtor
 * @param {Array<any>} selectedDebtors - Selected debtors details
 * @returns {boolean} Is retained debtor change
 */
export const isOtherDebtorRetained  = (selectedDebtors: Array<any>) : boolean =>{
  try{
    return selectedDebtors?.filter(
      ({ isSelectedRetaining, isCaseDebtor }: any) =>
        isSelectedRetaining && isCaseDebtor
    )?.length == 0
  }
  catch{
    return false
  }
}

/**
 * @description Get Selected debtor list
 * @param {Array<DuplicateDebtor>} duplicatingDebtors Duplicate debtor list
 * @param {Array<number>} selectedDebtors Selected debtor entrole id
 * @returns 
 */
export const getSelectedDebtorList = (
  duplicatingDebtors: Array<DuplicateDebtor>,
  selectedDebtors: Array<number>
): Array<any> => {
  try {
    return duplicatingDebtors
      ?.filter(
        ({ entRoleId, isSelectedRetaining }: any) =>
          selectedDebtors?.includes(entRoleId) || isSelectedRetaining
      )
      .map(({ entRoleId, isSelectedRetaining, isCaseDebtor }: any) => ({
        entRoleId,
        isSelectedRetaining,
        isCaseDebtor,
      }));
  } catch {
    return [];
  }
};
